import React, { useEffect, useState } from "react";
import * as yup from "yup";
import { yupResolver } from "@hookform/resolvers/yup";
import ModalComponent from "../modalComponent";
import { useForm } from "react-hook-form";
import TextInput from "../textInput";
import ButtonComponent from "../button";
import {
  callApiAsync,
  createPayment,
  getPaymentsByIndependentCost,
  deletePayment,
} from "../../services/apiService";
import ToastNotification from "../toastNotification";
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Card,
  CardContent,
  Chip,
  Alert,
  IconButton,
  Tooltip,
  LinearProgress,
  Fade,
  Slide,
  CircularProgress,
} from "@mui/material";
import {
  Payment,
  Delete,
  CheckCircle,
  Warning,
  TrendingUp,
  AccountBalance,
  Close,
  Error,
  Info,
} from "@mui/icons-material";
import DatePickerInput from "../datepickerInput";
import AlertComponent from "../alertModal";
import {
  CURRENCY_COP,
  formatCurrencyAmount,
} from "../../utils/currencyUtils";

// Animation timeouts
const ANIMATION_TIMEOUTS = {
  FADE: 300,
  SLIDE: 400,
  VALIDATION: 200,
};

const PaymentModal = ({
  open,
  onClose,
  independentCost,
  getAddittionalCostDataTable,
}) => {
  const [payments, setPayments] = useState([]);
  const [totalPaid, setTotalPaid] = useState(0);
  const [remainingAmount, setRemainingAmount] = useState(0);
  const [deleteAlertOpen, setDeleteAlertOpen] = useState(false);
  const [selectedPayment, setSelectedPayment] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [toastOpen, setToastOpen] = useState(false);
  const [toastMessage, setToastMessage] = useState("");
  const [toastSeverity, setToastSeverity] = useState("error");

  const costCurrency = independentCost?.currency ?? CURRENCY_COP;

  const schema = yup.object().shape({
    paymentDate: yup.date().required("La fecha de pago es obligatoria"),
    paymentAmount: yup
      .number()
      .typeError("El monto debe ser un número válido")
      .positive("El monto debe ser mayor a 0")
      .max(
        remainingAmount,
        `El monto no puede exceder ${formatCurrencyAmount(remainingAmount, costCurrency)}`
      )
      .required("El monto es obligatorio"),
  });

  const { control, handleSubmit, reset, watch } = useForm({
    resolver: yupResolver(schema),
    mode: "onChange",
    defaultValues: {
      paymentDate: new Date(),
      paymentAmount: 0,
    },
  });

  const watchedAmount = watch("paymentAmount");

  const onCloseModal = () => {
    reset();
    onClose();
  };

  const getPaymentsData = async () => {
    if (!independentCost?.id) return;

    await callApiAsync(
      () => getPaymentsByIndependentCost(independentCost.id),
      (data) => {
        setPayments(data || []);
        const total =
          data?.reduce(
            (sum, payment) => sum + (payment.paymentAmount || 0),
            0
          ) || 0;
        setTotalPaid(total);
        setRemainingAmount(((independentCost.price + (independentCost.tax || 0)) || 0) - total);
      }
    );
  };

  const handleFormSubmit = async (formData) => {
    if (independentCost?.isPaid) {
      setToastMessage(
        "No se puede agregar pagos a un costo independiente que ya está marcado como pagado"
      );
      setToastSeverity("warning");
      setToastOpen(true);
      return;
    }

    setIsSubmitting(true);

    try {
      const dataToSend = {
        independentCostId: independentCost.id,
        paymentDate: formData.paymentDate,
        paymentAmount: formData.paymentAmount,
      };

      await callApiAsync(
        () => createPayment(dataToSend),
        (data) => {
          getPaymentsData();
          getAddittionalCostDataTable();
          reset();
        }
      );
    } catch (error) {
      console.error("Error creating payment:", error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDeletePayment = (payment) => {
    if (independentCost?.isPaid) {
      setToastMessage(
        "No se puede eliminar pagos de un costo independiente que ya está marcado como pagado"
      );
      setToastSeverity("warning");
      setToastOpen(true);
      return;
    }

    setSelectedPayment(payment);
    setDeleteAlertOpen(true);
  };

  const confirmDeletePayment = async () => {
    if (!selectedPayment) return;

    await callApiAsync(
      () => deletePayment(selectedPayment.id),
      (data) => {
        getPaymentsData();
        getAddittionalCostDataTable();
        setDeleteAlertOpen(false);
        setSelectedPayment(null);
      }
    );
  };

  useEffect(() => {
    if (open && independentCost) {
      getPaymentsData();
    }
  }, [open, independentCost]);

  const formatAmount = (amount) =>
    formatCurrencyAmount(amount, costCurrency);

  const paymentProgress =
    independentCost?.price > 0 ? (totalPaid / (independentCost.price + (independentCost.tax || 0))) * 100 : 0;
  const isFullyPaid = remainingAmount <= 0;

  const isValidAmount = watchedAmount > 0 && watchedAmount <= remainingAmount;
  const isAmountExceeded = watchedAmount > remainingAmount;
  const isDisabled =
    !isValidAmount || isFullyPaid || isSubmitting || independentCost?.isPaid;

  return (
    <>
      <ModalComponent
        customWidth="1000px"
        open={open}
        onClose={onCloseModal}
        title={
          <Box
            display="flex"
            alignItems="center"
            justifyContent="space-between"
            width="100%"
          >
            <Box display="flex" alignItems="center" gap={2}>
              <Box
                sx={{
                  background:
                    "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
                  borderRadius: "50%",
                  p: 1,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <Payment sx={{ color: "white", fontSize: 28 }} />
              </Box>
              <Box>
                <Typography variant="h5" fontWeight="bold" color="primary">
                  Gestión de Pagos
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {independentCost?.name || "Costo Independiente"}
                </Typography>
              </Box>
            </Box>
            <Chip
              icon={isFullyPaid ? <CheckCircle /> : <Warning />}
              label={isFullyPaid ? "Pagado" : "Pago Pendiente"}
              color={isFullyPaid ? "success" : "warning"}
              variant="filled"
              sx={{ fontWeight: "bold" }}
            />
          </Box>
        }
      >
        <Fade in={open} timeout={ANIMATION_TIMEOUTS.FADE}>
          <Box>
            <Card
              sx={{
                mb: 3,
                background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
                color: "white",
                borderRadius: "16px",
                boxShadow: "0 8px 32px rgba(102, 126, 234, 0.3)",
                overflow: "hidden",
                position: "relative",
              }}
            >
              <Box
                sx={{
                  position: "absolute",
                  top: 0,
                  right: 0,
                  width: "200px",
                  height: "200px",
                  background: "rgba(255,255,255,0.1)",
                  borderRadius: "50%",
                  transform: "translate(50%, -50%)",
                }}
              />
              <CardContent sx={{ position: "relative", zIndex: 1 }}>
                <Box
                  display="flex"
                  justifyContent="space-between"
                  alignItems="center"
                >
                  <Box>
                    <Typography variant="h6" fontWeight="bold" mb={2}>
                      Resumen de Pagos
                    </Typography>
                    <Box display="flex" gap={4}>
                      <Box display="flex" alignItems="center" gap={1}>
                        <AccountBalance sx={{ fontSize: 20 }} />
                        <Box>
                          <Typography variant="body2" sx={{ opacity: 0.9 }}>
                            Precio Total
                          </Typography>
                          <Typography variant="h6" fontWeight="bold">
                            {formatAmount(independentCost?.price + (independentCost?.tax || 0) || 0)}
                          </Typography>
                        </Box>
                      </Box>
                      <Box display="flex" alignItems="center" gap={1}>
                        <TrendingUp sx={{ fontSize: 20 }} />
                        <Box>
                          <Typography variant="body2" sx={{ opacity: 0.9 }}>
                            Total Pagado
                          </Typography>
                          <Typography variant="h6" fontWeight="bold">
                            {formatAmount(totalPaid)}
                          </Typography>
                        </Box>
                      </Box>
                    </Box>
                  </Box>
                  <Box textAlign="right">
                    <Typography variant="h4" fontWeight="bold" mb={1}>
                      {formatAmount(Math.abs(remainingAmount))}
                    </Typography>
                    <Typography variant="body1" sx={{ opacity: 0.9 }}>
                      {remainingAmount > 0 ? "Restante por Pagar" : "Excedente"}
                    </Typography>
                    <LinearProgress
                      variant="determinate"
                      value={Math.min(paymentProgress, 100)}
                      sx={{
                        mt: 2,
                        height: 8,
                        borderRadius: 4,
                        backgroundColor: "rgba(255,255,255,0.3)",
                        "& .MuiLinearProgress-bar": {
                          borderRadius: 4,
                          background:
                            "linear-gradient(90deg, #4CAF50, #8BC34A)",
                        },
                      }}
                    />
                    <Typography
                      variant="caption"
                      sx={{ opacity: 0.8, mt: 1, display: "block" }}
                    >
                      {paymentProgress.toFixed(1)}% Completado
                    </Typography>
                  </Box>
                </Box>
              </CardContent>
            </Card>

            <Slide direction="up" in={true} timeout={ANIMATION_TIMEOUTS.SLIDE}>
              <Card
                sx={{
                  mb: 3,
                  borderRadius: "16px",
                  boxShadow: "0 4px 20px rgba(0,0,0,0.1)",
                }}
              >
                <CardContent sx={{ p: 3 }}>
                  <Typography
                    variant="h6"
                    fontWeight="bold"
                    mb={3}
                    display="flex"
                    alignItems="center"
                    gap={1}
                  >
                    Agregar Nuevo Pago
                  </Typography>

                  <form onSubmit={handleSubmit(handleFormSubmit)}>
                    <Box display="flex" gap={3} alignItems="flex-end">
                      <Box flex={1}>
                        <DatePickerInput
                          name="paymentDate"
                          control={control}
                          label="Fecha de Pago"
                          width="100%"
                          disabled={
                            isSubmitting ||
                            isFullyPaid ||
                            independentCost?.isPaid
                          }
                        />
                      </Box>
                      <Box flex={1}>
                        <TextInput
                          name="paymentAmount"
                          control={control}
                          label="Monto del Pago"
                          type="number"
                          width="100%"
                          disabled={
                            isSubmitting ||
                            isFullyPaid ||
                            independentCost?.isPaid
                          }
                          placeholder={`Máximo: ${formatAmount(
                            remainingAmount
                          )}`}
                          max={remainingAmount}
                          min={0}
                        />
                      </Box>
                      <ButtonComponent
                        type={independentCost?.isPaid ? "button" : "submit"}
                        backgroundColor={
                          independentCost?.isPaid
                            ? "#ccc"
                            : isDisabled
                            ? "#ccc"
                            : "#4CAF50"
                        }
                        textColor="#FFFFFF"
                        width="180px"
                        disabled={isDisabled}
                        sx={{
                          height: "56px",
                          borderRadius: "12px",
                          fontWeight: "bold",
                          fontSize: "1rem",
                          boxShadow: independentCost?.isPaid
                            ? "0 4px 16px rgba(204, 204, 204, 0.3)"
                            : "0 4px 16px rgba(76, 175, 80, 0.3)",
                          transition: "all 0.3s ease",
                          "&:hover": {
                            backgroundColor: independentCost?.isPaid
                              ? "#ccc"
                              : isDisabled
                              ? "#ccc"
                              : "#45a049",
                            transform: independentCost?.isPaid
                              ? "none"
                              : isDisabled
                              ? "none"
                              : "translateY(-2px)",
                            boxShadow: independentCost?.isPaid
                              ? "0 4px 16px rgba(204, 204, 204, 0.3)"
                              : isDisabled
                              ? "0 4px 16px rgba(76, 175, 80, 0.3)"
                              : "0 8px 24px rgba(76, 175, 80, 0.4)",
                          },
                        }}
                      >
                        <Box display="flex" alignItems="center" gap={1}>
                          {isSubmitting ? (
                            <CircularProgress size={20} color="inherit" />
                          ) : (
                            <Payment />
                          )}
                          {isSubmitting ? "Guardando..." : "Agregar Pago"}
                        </Box>
                      </ButtonComponent>
                    </Box>
                  </form>
                </CardContent>
              </Card>
            </Slide>

            {/* Payment History */}
            <Slide
              direction="up"
              in={true}
              timeout={ANIMATION_TIMEOUTS.SLIDE + 100}
            >
              <Card
                sx={{
                  borderRadius: "16px",
                  boxShadow: "0 4px 20px rgba(0,0,0,0.1)",
                }}
              >
                <CardContent sx={{ p: 3 }}>
                  <Typography
                    variant="h6"
                    fontWeight="bold"
                    mb={3}
                    display="flex"
                    alignItems="center"
                    gap={1}
                  >
                    Historial de Pagos
                  </Typography>

                  <TableContainer
                    component={Paper}
                    sx={{
                      maxHeight: 400,
                      borderRadius: "12px",
                      boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
                      overflow: "hidden",
                    }}
                  >
                    <Table stickyHeader>
                      <TableHead>
                        <TableRow sx={{ backgroundColor: "#f8f9fa" }}>
                          <TableCell
                            sx={{
                              fontWeight: "bold",
                              fontSize: "0.9rem",
                              color: "#495057",
                            }}
                          >
                            <Box display="flex" alignItems="center" gap={1}>
                              Fecha
                            </Box>
                          </TableCell>
                          <TableCell
                            sx={{
                              fontWeight: "bold",
                              fontSize: "0.9rem",
                              color: "#495057",
                            }}
                          >
                            <Box display="flex" alignItems="center" gap={1}>
                              Monto
                            </Box>
                          </TableCell>
                          <TableCell
                            sx={{
                              fontWeight: "bold",
                              fontSize: "0.9rem",
                              color: "#495057",
                            }}
                            align="center"
                          >
                            Acciones
                          </TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {payments.map((payment) => (
                          <TableRow
                            key={payment.id}
                            hover
                            sx={{
                              transition: "all 0.2s ease",
                              "&:hover": {
                                backgroundColor: "#f8f9fa",
                                transform: "scale(1.01)",
                              },
                            }}
                          >
                            <TableCell>
                              <Typography
                                variant="body2"
                                fontWeight="500"
                                color="#495057"
                              >
                                {new Date(
                                  payment.paymentDate
                                ).toLocaleDateString("es-CO", {
                                  year: "numeric",
                                  month: "long",
                                  day: "numeric",
                                })}
                              </Typography>
                            </TableCell>
                            <TableCell>
                              <Typography
                                variant="body2"
                                fontWeight="bold"
                                color="success.main"
                                fontSize="1.1rem"
                              >
                                {formatAmount(payment.paymentAmount)}
                              </Typography>
                            </TableCell>
                            <TableCell align="center">
                              <Tooltip
                                title={
                                  independentCost?.isPaid
                                    ? "No se puede eliminar pagos de un costo pagado"
                                    : "Eliminar Pago"
                                }
                                arrow
                              >
                                <IconButton
                                  onClick={() => handleDeletePayment(payment)}
                                  color="error"
                                  size="medium"
                                  disabled={independentCost?.isPaid}
                                  sx={{
                                    transition: "all 0.2s ease",
                                    opacity: independentCost?.isPaid ? 0.5 : 1,
                                    "&:hover": {
                                      backgroundColor: independentCost?.isPaid
                                        ? "transparent"
                                        : "rgba(244, 67, 54, 0.1)",
                                      transform: independentCost?.isPaid
                                        ? "none"
                                        : "scale(1.1) rotate(5deg)",
                                    },
                                  }}
                                >
                                  <Delete />
                                </IconButton>
                              </Tooltip>
                            </TableCell>
                          </TableRow>
                        ))}
                        {payments.length === 0 && (
                          <TableRow>
                            <TableCell
                              colSpan={3}
                              align="center"
                              sx={{ py: 6 }}
                            >
                              <Box
                                display="flex"
                                flexDirection="column"
                                alignItems="center"
                                gap={2}
                              >
                                <Box
                                  sx={{
                                    background:
                                      "linear-gradient(135deg, #e3f2fd, #bbdefb)",
                                    borderRadius: "50%",
                                    p: 2,
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                  }}
                                >
                                  <Payment
                                    sx={{ fontSize: 48, color: "#1976d2" }}
                                  />
                                </Box>
                                <Typography
                                  variant="body1"
                                  color="text.secondary"
                                  fontWeight="500"
                                >
                                  No hay pagos registrados
                                </Typography>
                                <Typography
                                  variant="body2"
                                  color="text.secondary"
                                >
                                  Agrega el primer pago usando el formulario de
                                  arriba
                                </Typography>
                              </Box>
                            </TableCell>
                          </TableRow>
                        )}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </CardContent>
              </Card>
            </Slide>
          </Box>
        </Fade>
      </ModalComponent>
      <AlertComponent
        open={deleteAlertOpen}
        onClose={() => setDeleteAlertOpen(false)}
        onConfirm={confirmDeletePayment}
        title="Confirmar Eliminación"
        message="¿Estás seguro de que deseas eliminar este pago?"
      />

      <ToastNotification
        open={toastOpen}
        handleClose={() => setToastOpen(false)}
        message={toastMessage}
        severity={toastSeverity}
      />
    </>
  );
};

export default PaymentModal;
